import { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "../lib/api";
import Button from "./ui/Button";
import { formatPrice } from "../lib/utils";

/**
 * Checkout for standalone purchases (gift cards, packages, memberships):
 * the customer picks how to pay, then we POST `body` + the payment fields
 * to `url` (see the backend's PurchaseCharger). Calls onPaid(responseData) on success —
 * with "Pay at store" the purchase comes back `pending` until the
 * business marks it paid. `allowCash={false}` hides that option (e.g. a
 * gift card not tied to one business, which no one could confirm).
 */
export default function PayDialog({ title, amount, url, body = {}, allowCash = true, onClose, onPaid }) {
  const [config, setConfig] = useState(null);
  const [provider, setProvider] = useState(null);
  const [testOutcome, setTestOutcome] = useState("success");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/payments/config")
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({}));
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const providers = [
    ...(allowCash ? [{ id: "cash", label: "Pay at store" }] : []),
    ...(config?.test_mode ? [{ id: "test", label: "Test payment" }] : []),
  ];

  // Default to the first option once we know which ones are available.
  const selected = providers.some((p) => p.id === provider) ? provider : providers[0]?.id;

  const pay = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post(url, {
        ...body,
        provider: selected,
        test_outcome: selected === "test" ? testOutcome : undefined,
      });

      onPaid(data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Payment could not be processed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-montserrat text-lg font-bold text-slate-900">{title}</h2>
            <p className="text-sm text-muted-foreground">{formatPrice(amount)}</p>
          </div>
          <button onClick={onClose} disabled={submitting} aria-label="Close" className="text-muted-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!config ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : !providers.length ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Online payment isn't available yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {providers.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setProvider(p.id)}
                  className={`rounded-xl border p-3 text-left text-sm ${selected === p.id ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {selected === "test" && (
              <div className="mt-3 flex gap-2">
                {[
                  { id: "success", label: "Approve" },
                  { id: "failed", label: "Decline" },
                ].map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setTestOutcome(o.id)}
                    className={`flex-1 rounded-xl border p-2 text-sm ${testOutcome === o.id ? "border-primary bg-primary/5" : "border-border"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}

            <p className="mt-2 text-xs text-muted-foreground">
              {selected === "cash"
                ? "Reserved now — it becomes active once you pay at the store and they confirm it."
                : "Test mode — settles instantly as approved or declined. No money is moved."}
            </p>

            {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

            <Button className="mt-4 w-full" onClick={pay} disabled={submitting}>
              {submitting ? "Processing…" : `Pay ${formatPrice(amount)}`}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
